import { ApiConfiguredNotification } from 'api/notification/types/ApiConfiguredNotification';
import { ApiMentionListingApplicationNotification } from 'api/notification/types/ApiMentionListingApplicationNotification';
import { ApiMentionSuggestedApplicantNotification } from 'api/notification/types/ApiMentionSuggestedApplicantNotification';
import { ApiNewFollowerNotification } from 'api/notification/types/ApiNewFollowerNotification';
import { ApiNewListingApplicationNotification } from 'api/notification/types/ApiNewListingApplicationNotification';
import { ApiNewSuggestedApplicantsNotification } from 'api/notification/types/ApiNewSuggestedApplicantsNotification';
import { ApiNotification } from 'api/notification/types/ApiNotification';
import { ApiOpenwebLikedCommentNotification } from 'api/notification/types/ApiOpenwebLikedCommentNotification';
import { ApiOpenwebRepliedCommentNotification } from 'api/notification/types/ApiOpenwebRepliedCommentNotification';
import { ApiOpenwebUserMentionedNotification } from 'api/notification/types/ApiOpenwebUserMentionedNotification';
import { ApplicantsByListingMappedNotification } from 'types/mappedNotifications/ApplicantsByListingMappedNotification';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { NewSuggestedApplicantsByJobMappedNotification } from 'types/mappedNotifications/NewSuggestedApplicantsByJobMappedNotification';
import { sortBy } from 'utils/functional/array';

export const getMappedNotificationIds = (
  notification: MappedNotification | ApiNotification,
) => {
  if (notification.type === 'NEW_APPLICATIONS_BY_LISTING') {
    return notification.data.applications.map(
      (application) => application.notificationId,
    );
  }
  if (notification.type === 'NEW_SUGGESTED_APPLICANTS_BY_JOB') {
    return notification.data.batches.map((batch) => batch.notificationId);
  }
  if (notification.type === 'MENTION_LISTING_APPLICATION') {
    return [notification.id];
  }
  if (notification.type === 'MENTION_SUGGESTED_APPLICANT') {
    return [notification.id];
  }
  if (notification.type === 'OPENWEB_REPLIED_COMMENT') {
    return [notification.id];
  }
  if (notification.type === 'OPENWEB_LIKED_COMMENT') {
    return [notification.id];
  }
  if (notification.type === 'OPENWEB_USER_MENTIONED') {
    return [notification.id];
  }
  if (notification.type === 'CONFIGURED_NOTIFICATION') {
    return [notification.id];
  }
  if (notification.type === 'NEW_FOLLOWER') {
    return [notification.id];
  }

  throw Error('Unknown notification type');
};

/*
 * Sort and group notifications.
 *
 * Notifications of type "NEW_LISTING_APPLICATION" and "NEW_SUGGESTED_APPLICANTS"
 * are grouped by listing id.
 *
 * All notifications are sorted by creation date.
 *
 */

export function sortNotifications(unsortedNotifications: ApiNotification[]) {
  const {
    applicantNotifications,
    suggestedApplicantsNotifications,
    applicantMentionNotifications,
    suggestedApplicantMentionNotifications,
    openwebRepliedCommentNotifications,
    openwebLikedCommentNotifications,
    openwebUserMentionedNotifications,
    configuredNotifications,
    newFollowerNotifications,
  } = unsortedNotifications.reduce(
    (acc, notification) => {
      if (notification.type === 'NEW_LISTING_APPLICATION') {
        acc.applicantNotifications.push(notification);
      } else if (notification.type === 'NEW_SUGGESTED_APPLICANTS') {
        acc.suggestedApplicantsNotifications.push(notification);
      } else if (notification.type === 'MENTION_LISTING_APPLICATION') {
        acc.applicantMentionNotifications.push(notification);
      } else if (notification.type === 'MENTION_SUGGESTED_APPLICANT') {
        acc.suggestedApplicantMentionNotifications.push(notification);
      } else if (notification.type === 'OPENWEB_REPLIED_COMMENT') {
        acc.openwebRepliedCommentNotifications.push(notification);
      } else if (notification.type === 'OPENWEB_LIKED_COMMENT') {
        acc.openwebLikedCommentNotifications.push(notification);
      } else if (notification.type === 'OPENWEB_USER_MENTIONED') {
        acc.openwebUserMentionedNotifications.push(notification);
      } else if (notification.type === 'CONFIGURED_NOTIFICATION') {
        acc.configuredNotifications.push(notification);
      } else if (notification.type === 'NEW_FOLLOWER') {
        acc.newFollowerNotifications.push(notification);
      }

      return acc;
    },
    {
      applicantNotifications: [] as ApiNewListingApplicationNotification[],
      suggestedApplicantsNotifications:
        [] as ApiNewSuggestedApplicantsNotification[],
      applicantMentionNotifications:
        [] as ApiMentionListingApplicationNotification[],
      suggestedApplicantMentionNotifications:
        [] as ApiMentionSuggestedApplicantNotification[],
      openwebRepliedCommentNotifications:
        [] as ApiOpenwebRepliedCommentNotification[],
      openwebLikedCommentNotifications:
        [] as ApiOpenwebLikedCommentNotification[],
      openwebUserMentionedNotifications:
        [] as ApiOpenwebUserMentionedNotification[],
      configuredNotifications: [] as ApiConfiguredNotification[],
      newFollowerNotifications: [] as ApiNewFollowerNotification[],
    },
  );

  const applicantNotificationsByListingId: Record<
    string,
    ApplicantsByListingMappedNotification
  > = applicantNotifications.reduce(
    (
      acc: Record<string, ApplicantsByListingMappedNotification>,
      notification,
    ) => {
      const notificationListing = notification.data.listingApplication.listing;
      const notificationForListing = acc[notificationListing.id] || {
        type: 'NEW_APPLICATIONS_BY_LISTING',
        data: {
          ...notificationListing,
          applications: [],
          orgId: notification.about.id,
          status: notification.data.listingApplication.status,
          count: 0,
        },
        read: notification.read,
        viewed: notification.viewed,
      };
      notificationForListing.data.count += 1;
      notificationForListing.created =
        acc[notificationListing.id] &&
        new Date(acc[notificationListing.id].created) >
          new Date(notification.created)
          ? acc[notificationListing.id].created
          : notification.created;
      notificationForListing.data.applications.push({
        id: notification.data.listingApplication.id,
        notificationId: notification.id,
      });
      return {
        ...acc,
        [notificationForListing.data.id]: notificationForListing,
      };
    },
    {},
  );

  const suggestedApplicantNotificationsByJobId: Record<
    string,
    NewSuggestedApplicantsByJobMappedNotification
  > = suggestedApplicantsNotifications.reduce(
    (
      acc: Record<string, NewSuggestedApplicantsByJobMappedNotification>,
      notification,
    ) => {
      const { job } = notification.data.suggestedApplicantBatch;

      const notificationForJob = acc[job.id] || {
        type: 'NEW_SUGGESTED_APPLICANTS_BY_JOB',
        data: {
          job,
          orgId: notification.about.id,
          count: 0,
          batches: [],
        },
        read: notification.read,
        viewed: notification.viewed,
      };
      notificationForJob.data.count +=
        notification.data.suggestedApplicantBatch.applicantCount;
      notificationForJob.created =
        acc[job.id] &&
        new Date(acc[job.id].created) > new Date(notification.created)
          ? acc[job.id].created
          : notification.created;
      notificationForJob.data.batches.push({
        id: notification.data.suggestedApplicantBatch.id,
        notificationId: notification.id,
      });
      return { ...acc, [notificationForJob.data.job.id]: notificationForJob };
    },
    {},
  );

  const notifications: (ApiNotification | MappedNotification)[] = [
    // Grouped
    ...Object.values(applicantNotificationsByListingId),
    ...Object.values(suggestedApplicantNotificationsByJobId),
    // Un-grouped
    ...Object.values(applicantMentionNotifications),
    ...Object.values(suggestedApplicantMentionNotifications),
    ...Object.values(openwebRepliedCommentNotifications),
    ...Object.values(openwebLikedCommentNotifications),
    ...Object.values(openwebUserMentionedNotifications),
    ...Object.values(configuredNotifications),
    ...Object.values(newFollowerNotifications),
  ];

  return sortBy(notifications, (n) => new Date(n.created), true);
}
